import axios from "axios";
import classNames from "classnames";
import { formRegEx, formRegExReplacer } from "constant/regex";
import moment from "moment";
import Papa from "papaparse";
import { toast } from "react-toastify";
import validator from "validator";
import { CURRENCY } from "../constant/constants";

export const apiHeader = (token, isFormData) => {
  const headers = {
    "timezone-offset": moment().utcOffset(),
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

export const formatMessageDate = (date) => {
  const messageDate = moment(date);
  const now = moment();

  if (messageDate.isSame(now, "minute")) {
    // If the date is exactly now, show the current time, e.g., 'HH:mm A'
    return now.format("HH:mm A");
  } else if (messageDate.isSame(now, "day")) {
    // If the date is today, use fromNow()
    return messageDate.fromNow();
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    // If the date is yesterday, use fromNow()
    return messageDate.fromNow();
  } else {
    // Otherwise, show the date in a standard format, e.g., 'MM/DD/YYYY'
    return messageDate.format("MM/DD/YYYY");
  }
};

export const formatDate = (date) => {
  const formattedDate = moment(date);
  return formattedDate.format("MM/DD/YYYY");
};

export const formatTime = (time) => {
  const formattedTime = moment(time);
  return formattedTime.format("hh:mm A");
};
export function stripAllHtmlTags(input) {
  return input?.replace(/<[^>]*>?/gm, "");
}

const options = {
  onDownloadProgress: () => {},
};

export function downloadFileFromUrl(fileUrl, filename) {
  axios
    .get(fileUrl, {
      responseType: "blob",
      ...options,
    })
    .then(function (response) {
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: response.headers["content-type"],
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
    })
    .catch((e) => {
      console.log("error", e);
    });
}

export const scrollToTop = (topVal = 0) => {
  window.scrollTo({
    top: topVal,
    behavior: "smooth",
  });
};

export const formatNumber = (num) => {
  if (num % 1 === 0) {
    return `${CURRENCY}${num?.toLocaleString()}`;
  } else {
    return `${CURRENCY}${num?.toFixed(2)?.toLocaleString()}`;
  }
};

export const quillValidateHandler = (
  params,
  toastMessage = "Please fill the home cms field!"
) => {
  let flag = true;
  for (let key in params) {
    const parsedHTML = new DOMParser().parseFromString(
      params[key],
      "text/html"
    );
    const plainText = parsedHTML.body.textContent || "";
    if (plainText?.trim() === "") {
      toast.error(toastMessage);
      flag = false;
      break;
    }
  }
  return flag;
};

export const cn = (...args) => classNames(...args);

export const validateDate = (date) => {
  if (!date) return null; // Return null if date is falsy

  // Check if date is a moment object and has a $d property
  if (date?.$d) {
    return moment(date.$d).format(); // Format if $d exists
  } else {
    return moment(date).format(); // Assume date is a valid input for moment
  }
};

export const validateUrl = (url) => {
  const urlRegex =
    /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  return urlRegex.test(url);
};

export const camelCaseToLower = (str) => {
  return str.replace(formRegEx, formRegExReplacer).toLowerCase();
};

export const getYearRange = () => {
  const years = [
    ...Array.from({ length: 5 }).map((_, i) => ({
      label: `${moment().subtract(i, "year").format("YYYY")}`,
      value: `${moment().subtract(i, "year").format("YYYY")}`,
    })),
  ];
  return years;
};

export const urlPattern =
  /^(https?:\/\/)(www\.)?([\w-]+\.)+[a-z]{2,6}(\/[\w-]*)*\/?$/;
export const RenderToast = ({ type = "error", message = "" }) => {
  let toastId = "error-render-toast";

  if (!type || !message) return;

  toast[type](message, {
    id: toastId,
  });
};

export const validateNestedParams = (
  params,
  parentKey = "",
  options = { disallowEmptyArray: true }
) => {
  const formatKey = (rawKey) => {
    return rawKey
      .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i) + 1}`) // [0] => 1
      .split(".")
      .map(
        (part) =>
          part.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) // capitalize
      )
      .join(" → ");
  };

  if (Array.isArray(params)) {
    if (options.disallowEmptyArray && params.length === 0) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (let i = 0; i < params.length; i++) {
      const key = `${parentKey}[${i}]`;
      if (!validateNestedParams(params[i], key, options)) return false;
    }
  } else if (typeof params === "object" && params !== null) {
    const keys = Object.keys(params);
    if (keys.length === 0 && parentKey) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (!validateNestedParams(params[key], fullKey, options)) return false;
    }
  } else {
    if (params === "" || params === null || params === undefined) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }
  }

  return true;
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const CreateFormData = (data) => {
  const formData = new FormData();
  for (let key in data) {
    formData.append(key, data[key]);
  }
  return formData;
};

export const getDaysInMonth = (monthName, year) => {
  const monthsList = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = monthsList.findIndex((m) => m === monthName);
  return new Date(year, monthIndex + 1, 0).getDate();
};
export const validateSocialLinks = (socialLinks) => {
  for (let index = 0; index < socialLinks.length; index++) {
    const link = socialLinks[index];
    if (!validator.isURL(link)) {
      RenderToast({
        type: "error",
        message: `${link} is not a valid URL`,
      });
      return false;
    }
  }
  return true;
};

export const getId = (id) => {
  return id.toString().padStart(6, "0");
};

export const generateCsv = (data, fields) => {
  const csv = Papa.unparse({
    fields: fields,
    data: data,
    quotes: true, // Wrap every field in quotes
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ",",
    header: true,
    newline: "\r\n",
    skipEmptyLines: false, // Skip empty lines
    columns: null, // Specify columns if you want to export only specific ones
  });

  return csv;
};

export const downloadCsv = (data, fieldsObj, fieldsArr, csvName) => {
  const dataToDownload = data?.map((item) => {
    const obj = {};
    fieldsArr?.forEach((field) => {
      obj[field] = item[fieldsObj[field]];
    });
    return obj;
  });
  const csv = generateCsv(dataToDownload, fieldsArr);
  const blob = new Blob([csv], {
    type: "text/csv",
  });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${csvName}.csv`;
  link.click();
};

export const extractItineraryImages = (itinerary, maxImages = 5) => {
  if (!itinerary?.city_details) return [];

  const allImages = [];

  Object.values(itinerary.city_details).forEach((city) => {
    if (Array.isArray(city?.images)) {
      allImages.push(...city.images);
    }
  });

  if (allImages.length === 0) return [];

  // 2. Shuffle images
  for (let i = allImages.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allImages[i], allImages[j]] = [allImages[j], allImages[i]];
  }

  const slicedImages = allImages.slice(0, maxImages);
  return slicedImages;
};

export const extractItineraryTitle = (itinerary) => {
  return itinerary?.itinerary_days?.title || "";
};

export const mapTabValueToApi = (tabValue) => {
  const mapping = {
    weekly: "week",
    monthly: "month",
    yearly: "year",
    week: "week",
    month: "month",
    year: "year",
  };
  return mapping[tabValue] || tabValue || "year";
};

export const transformActiveUsersData = (activeUsersArray, period) => {
  if (!activeUsersArray || !Array.isArray(activeUsersArray)) {
    return [];
  }

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (period === "week" || period === "weekly") {
    // 7 items for week (Mon-Sun)
    return activeUsersArray.slice(0, 7).map((value, index) => ({
      month: daysOfWeek[index] || `Day ${index + 1}`,
      value: value || 0,
    }));
  } else if (period === "month" || period === "monthly") {
    // 30 items for month (days 1-30)
    return activeUsersArray.slice(0, 30).map((value, index) => ({
      month: `${index + 1}`,
      value: value || 0,
    }));
  } else if (period === "year" || period === "yearly") {
    // 5 items for year (last 5 years)
    const currentYear = new Date().getFullYear();
    return activeUsersArray.slice(0, 5).map((value, index) => {
      const year = currentYear - (4 - index);
      return {
        month: `${year}`,
        value: value || 0,
      };
    });
  }

  return [];
};

export const transformAgeGroupData = (ageGroupObject) => {
  if (!ageGroupObject || typeof ageGroupObject !== "object") {
    return [];
  }

  // Map API keys to display names
  const keyMapping = {
    under_18: "Under 18",
    "18_24": "18-24",
    "25_34": "25-34",
    "35_44": "35-44",
    "45_54": "45-54",
    "55_64": "55-64",
    over_65: "65+",
  };

  // Define the order for display
  const displayOrder = [
    "under_18",
    "18_24",
    "25_34",
    "35_44",
    "45_54",
    "55_64",
    "over_65",
  ];

  return displayOrder
    .map((key) => {
      if (ageGroupObject.hasOwnProperty(key)) {
        return {
          name: keyMapping[key] || key,
          value: ageGroupObject[key] || 0,
        };
      }
      return null;
    })
    .filter((item) => item !== null);
};

export const transformGenderGroupData = (genderGroupObject) => {
  if (!genderGroupObject || typeof genderGroupObject !== "object") {
    return [];
  }

  return [
    {
      name: "Male",
      value: genderGroupObject.male || 0,
    },
    {
      name: "Female",
      value: genderGroupObject.female || 0,
    },
  ];
};

export const transformDeviceGroupData = (deviceGroupObject) => {
  if (!deviceGroupObject || typeof deviceGroupObject !== "object") {
    return [];
  }

  // Calculate total for percentage calculation
  const androidValue = deviceGroupObject.android || 0;
  const iosValue = deviceGroupObject.ios || 0;
  const total = androidValue + iosValue;

  // Calculate percentages
  const androidPercent =
    total > 0 ? Math.round((androidValue / total) * 100) : 0;
  const iosPercent = total > 0 ? Math.round((iosValue / total) * 100) : 0;

  return [
    {
      name: "Android",
      value: androidPercent,
      color: "#00CFE8",
    },
    {
      name: "iOS",
      value: iosPercent,
      color: "#FFC107",
    },
  ];
};

export const transformCountryGroupData = (countryGroupObject) => {
  if (!countryGroupObject || typeof countryGroupObject !== "object") {
    return [];
  }

  // Transform object to array format for ImpressionsComponent
  // Format: [{ country: "Pakistan", impressions: 20 }, ...]
  return Object.entries(countryGroupObject)
    .map(([key, value]) => ({
      country: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
      impressions: value || 0,
    }))
    .sort((a, b) => b.impressions - a.impressions); // Sort by impressions descending
};
